import React, { useState, useEffect } from 'react';
import { useToast } from '../../Toast';
import { useAuth } from '../../../contexts/AuthContext';
import { db } from '../../../config/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

interface Product {
  id: string;
  name: string;
  price: number;
}

interface Service {
  id: string;
  code: string;
  name: string;
  description: string;
  duration: number;
  rate: number;
  isDefault: boolean;
  availableForOnlineRequests: boolean;
  allowForNewClients: boolean;
  requireCallToRequest: boolean;
  blockOffBefore: number;
  blockOffAfter: number;
  active: boolean;
}

const defaultProducts: Product[] = [
  { id: '1', name: 'Therapy Workbook', price: 25 },
  { id: '2', name: 'Initial client balance', price: 0 },
  { id: '3', name: 'Assessment Materials', price: 50 }
];

const ServicesProducts: React.FC = () => {
  const toast = useToast();
  const { user } = useAuth();
  // Products state
  const [products, setProducts] = useState<Product[]>(defaultProducts);

  // Load from Firestore on mount
  useEffect(() => {
    if (!user) return;
    const loadSettings = async () => {
      try {
        const docRef = doc(db, 'billing_settings', 'services_products');
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          const data = snap.data();
          if (data.products) setProducts(data.products);
          // Services/inactiveServices could be loaded here too if persisted
        }
      } catch (err) {
        console.error('Error loading services/products settings:', err);
      }
    };
    loadSettings();
  }, [user]);

  // Services state
  const [services, setServices] = useState<Service[]>([
    {
      id: '1',
      code: '90834',
      name: 'Psychotherapy, 45 min',
      description: '',
      duration: 50,
      rate: 145,
      isDefault: true,
      availableForOnlineRequests: true,
      allowForNewClients: false,
      requireCallToRequest: false,
      blockOffBefore: 0,
      blockOffAfter: 0,
      active: true
    },
    {
      id: '2',
      code: '90847',
      name: 'Family psychotherapy, conjoint psychotherapy with the patient present',
      description: '',
      duration: 50,
      rate: 165,
      isDefault: false,
      availableForOnlineRequests: false,
      allowForNewClients: false,
      requireCallToRequest: false,
      blockOffBefore: 0,
      blockOffAfter: 0,
      active: true
    },
    {
      id: '3',
      code: '+90838',
      name: 'Psychotherapy Add-on, 60 min',
      description: '',
      duration: 50,
      rate: 0,
      isDefault: false,
      availableForOnlineRequests: false,
      allowForNewClients: false,
      requireCallToRequest: false,
      blockOffBefore: 0,
      blockOffAfter: 0,
      active: true
    }
  ]);

  const [inactiveServices, setInactiveServices] = useState<Service[]>([
    {
      id: '4',
      code: '00000',
      name: 'Initial Consultation - No Charge',
      description: '',
      duration: 15,
      rate: 0,
      isDefault: false,
      availableForOnlineRequests: false,
      allowForNewClients: false,
      requireCallToRequest: false,
      blockOffBefore: 0,
      blockOffAfter: 0,
      active: false
    },
    {
      id: '5',
      code: '90791',
      name: 'Psychiatric Diagnostic Evaluation',
      description: '',
      duration: 50,
      rate: 100,
      isDefault: false,
      availableForOnlineRequests: false,
      allowForNewClients: false,
      requireCallToRequest: false,
      blockOffBefore: 0,
      blockOffAfter: 0,
      active: false
    },
    {
      id: '6',
      code: '90837',
      name: 'Psychotherapy, 60 min',
      description: '',
      duration: 60,
      rate: 155,
      isDefault: false,
      availableForOnlineRequests: false,
      allowForNewClients: false,
      requireCallToRequest: false,
      blockOffBefore: 0,
      blockOffAfter: 0,
      active: false
    },
    {
      id: '7',
      code: '90853',
      name: 'Group Therapy',
      description: '',
      duration: 50,
      rate: 100,
      isDefault: false,
      availableForOnlineRequests: false,
      allowForNewClients: false,
      requireCallToRequest: false,
      blockOffBefore: 0,
      blockOffAfter: 0,
      active: false
    },
    {
      id: '8',
      code: '97110',
      name: 'Therapeutic exercises',
      description: '',
      duration: 50,
      rate: 120,
      isDefault: false,
      availableForOnlineRequests: false,
      allowForNewClients: false,
      requireCallToRequest: false,
      blockOffBefore: 0,
      blockOffAfter: 0,
      active: false
    },
    {
      id: '9',
      code: '97530',
      name: 'Therapeutic activities, direct (one-on-one) patient contact (use of dynamic activities to improve functional performance), each 15 minutes',
      description: '',
      duration: 50,
      rate: 100,
      isDefault: false,
      availableForOnlineRequests: false,
      allowForNewClients: false,
      requireCallToRequest: false,
      blockOffBefore: 0,
      blockOffAfter: 0,
      active: false
    }
  ]);

  const [showInactiveServices, setShowInactiveServices] = useState(false);
  const [showAddServiceModal, setShowAddServiceModal] = useState(false);

  // New service form state
  const [newService, setNewService] = useState<Partial<Service>>({
    code: '12345',
    description: '',
    rate: 0,
    duration: 50,
    isDefault: false,
    availableForOnlineRequests: true,
    allowForNewClients: false,
    requireCallToRequest: false,
    blockOffBefore: 0,
    blockOffAfter: 0,
    active: true
  });

  const handleAddProduct = () => {
    setProducts([...products, { id: Date.now().toString(), name: '', price: 0 }]);
  };

  const handleDeleteProduct = (id: string) => {
    setProducts(products.filter(p => p.id !== id));
  };

  const handleAddService = () => {
    setShowAddServiceModal(true);
  };

  const handleSaveNewService = () => {
    const service: Service = {
      id: Date.now().toString(),
      code: newService.code || '',
      name: newService.name || '',
      description: newService.description || '',
      duration: newService.duration || 50,
      rate: newService.rate || 0,
      isDefault: newService.isDefault || false,
      availableForOnlineRequests: newService.availableForOnlineRequests || false,
      allowForNewClients: newService.allowForNewClients || false,
      requireCallToRequest: newService.requireCallToRequest || false,
      blockOffBefore: newService.blockOffBefore || 0,
      blockOffAfter: newService.blockOffAfter || 0,
      active: true
    };
    setServices([...services, service]);
    setShowAddServiceModal(false);
    setNewService({
      code: '12345',
      description: '',
      rate: 0,
      duration: 50,
      isDefault: false,
      availableForOnlineRequests: true,
      allowForNewClients: false,
      requireCallToRequest: false,
      blockOffBefore: 0,
      blockOffAfter: 0,
      active: true
    });
  };

  const handleSaveProducts = async () => {
    try {
      const docRef = doc(db, 'billing_settings', 'services_products');
      await setDoc(docRef, {
        products,
        services,
        inactiveServices,
        ownerId: user?.uid || '',
        updatedAt: new Date().toISOString()
      }, { merge: true });
      toast.success('Products saved successfully!');
    } catch (err) {
      console.error('Error saving products:', err);
      toast.error('Failed to save products.');
    }
  };

  return (
    <div style={{ padding: '32px', maxWidth: '1400px' }}>
      {/* Products Section */}
      <section style={{ marginBottom: '40px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#FFFFFF', margin: '0 0 4px 0' }}>
              Products
            </h1>
            <p style={{ fontSize: '13px', color: '#87CEEB', margin: 0 }}>
              Manage products and set prices
            </p>
          </div>
          <button
            onClick={handleSaveProducts}
            style={{
              backgroundColor: '#1E90FF',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '6px',
              padding: '8px 20px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Save changes
          </button>
        </div>

        <div style={{ textAlign: 'right', marginBottom: '16px' }}>
          <span style={{ color: '#1E90FF', fontSize: '13px' }}>
            📹 Watch a quick video about Products
          </span>
        </div>

        {/* Products Table */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px 50px', gap: '16px', marginBottom: '12px' }}>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#FFFFFF' }}>Product Name</div>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#FFFFFF' }}>Price</div>
            <div></div>
          </div>

          {products.map((product, index) => (
            <div key={product.id} style={{ display: 'grid', gridTemplateColumns: '1fr 200px 50px', gap: '16px', marginBottom: '12px', alignItems: 'center' }}>
              <input
                type="text"
                value={product.name}
                onChange={(e) => {
                  const updated = [...products];
                  updated[index].name = e.target.value;
                  setProducts(updated);
                }}
                placeholder={index === 1 ? 'Initial client balance' : ''}
                style={{
                  padding: '10px 12px',
                  backgroundColor: '#0F4C75',
                  border: '1px solid #6CA6C1',
                  borderRadius: '4px',
                  color: '#FFFFFF',
                  fontSize: '14px',
                  width: '100%'
                }}
              />
              <input
                type="text"
                value={`$${product.price}`}
                onChange={(e) => {
                  const updated = [...products];
                  const value = e.target.value.replace('$', '');
                  updated[index].price = parseFloat(value) || 0;
                  setProducts(updated);
                }}
                style={{
                  padding: '10px 12px',
                  backgroundColor: '#0F4C75',
                  border: '1px solid #6CA6C1',
                  borderRadius: '4px',
                  color: '#FFFFFF',
                  fontSize: '14px',
                  width: '100%'
                }}
              />
              {index > 0 && (
                <button
                  onClick={() => handleDeleteProduct(product.id)}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#FF6B6B',
                    fontSize: '18px',
                    cursor: 'pointer',
                    padding: '4px'
                  }}
                >
                  🗑️
                </button>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Services Section */}
      <section>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#FFFFFF', margin: '0 0 4px 0' }}>
              Services
            </h1>
            <p style={{ fontSize: '13px', color: '#87CEEB', margin: 0 }}>
              Manage services and set rates
            </p>
          </div>
          <button
            onClick={handleAddService}
            style={{
              backgroundColor: '#1E90FF',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '6px',
              padding: '8px 20px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Add service
          </button>
        </div>

        <div style={{ textAlign: 'right', marginBottom: '16px' }}>
          <span style={{ color: '#1E90FF', fontSize: '13px' }}>
            📹 Watch a quick video about Services
          </span>
        </div>

        {/* Info Box */}
        <div style={{
          backgroundColor: '#E3F2FD',
          border: '1px solid #90CAF9',
          borderRadius: '6px',
          padding: '16px',
          marginBottom: '24px'
        }}>
          <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#0D47A1', margin: '0 0 8px 0' }}>
            Click on each Service name to edit
          </h3>
          <p style={{ fontSize: '13px', color: '#1565C0', margin: 0 }}>
            Service Descriptions are shown throughout the MyClinicCore platform internally, in some client communications and in superbills.
          </p>
        </div>

        {/* Services Table Header */}
        <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr 200px', gap: '16px', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #6CA6C1' }}>
          <div style={{ fontSize: '14px', fontWeight: 600, color: '#FFFFFF' }}>Services</div>
          <div></div>
          <div style={{ fontSize: '14px', fontWeight: 600, color: '#FFFFFF', textAlign: 'right' }}>Appointment requests</div>
        </div>

        {/* Active Services List */}
        {services.map((service) => (
          <div
            key={service.id}
            style={{
              display: 'grid',
              gridTemplateColumns: '100px 1fr 200px',
              gap: '16px',
              padding: '16px 0',
              borderBottom: '1px solid #6CA6C1',
              cursor: 'pointer'
            }}
          >
            <div style={{ fontSize: '14px', color: '#1E90FF', fontWeight: 500 }}>{service.code}</div>
            <div>
              <div style={{ fontSize: '14px', color: '#1E90FF', fontWeight: 500, marginBottom: '4px' }}>
                {service.name}
              </div>
              <div style={{ fontSize: '13px', color: '#E0E0E0', marginBottom: '4px' }}>
                {service.duration} minutes at ${service.rate}
              </div>
              {service.isDefault && (
                <div style={{ fontSize: '13px', color: '#4CAF50' }}>
                  ✓ Default practice service
                </div>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', alignItems: 'center' }}>
              {service.availableForOnlineRequests && (
                <span
                  style={{
                    backgroundColor: '#000000',
                    color: '#FFFFFF',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Available for online appointment requests
                </span>
              )}
              {service.allowForNewClients && (
                <span
                  style={{
                    backgroundColor: '#000000',
                    color: '#FFFFFF',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Allow for New Clients
                </span>
              )}
              <button
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: '#87CEEB',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                📅
              </button>
              <button
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: '#87CEEB',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                👤
              </button>
            </div>
          </div>
        ))}

        {/* Inactive Services Toggle */}
        <div style={{ marginTop: '24px' }}>
          <button
            onClick={() => setShowInactiveServices(!showInactiveServices)}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: '#1E90FF',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              textDecoration: 'underline',
              padding: 0
            }}
          >
            Inactive Services ({showInactiveServices ? 'Hide' : 'Show'})
          </button>
        </div>

        {/* Inactive Services List */}
        {showInactiveServices && (
          <div style={{ marginTop: '24px' }}>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#FFFFFF', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #6CA6C1' }}>
              Services
            </div>

            {inactiveServices.map((service) => (
              <div
                key={service.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '100px 1fr',
                  gap: '16px',
                  padding: '16px 0',
                  borderBottom: '1px solid #6CA6C1',
                  cursor: 'pointer'
                }}
              >
                <div style={{ fontSize: '14px', color: '#1E90FF', fontWeight: 500 }}>{service.code}</div>
                <div>
                  <div style={{ fontSize: '14px', color: '#1E90FF', fontWeight: 500, marginBottom: '4px' }}>
                    {service.name}
                  </div>
                  <div style={{ fontSize: '13px', color: '#E0E0E0' }}>
                    {service.duration} minutes at ${service.rate}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Add Service Modal */}
      {showAddServiceModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            zIndex: 1000,
            overflowY: 'auto',
            padding: '40px 20px'
          }}
          onClick={() => setShowAddServiceModal(false)}
        >
          <div
            style={{
              backgroundColor: '#0A2540',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '900px',
              width: '100%',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#FFFFFF', margin: 0 }}>
                Services
              </h2>
              <button
                onClick={() => setShowAddServiceModal(false)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: '#87CEEB',
                  fontSize: '14px',
                  cursor: 'pointer',
                  padding: '4px 8px'
                }}
              >
                Cancel
              </button>
            </div>

            <p style={{ fontSize: '13px', color: '#87CEEB', marginBottom: '20px' }}>
              Manage services and set rates
            </p>

            <div style={{ textAlign: 'right', marginBottom: '16px' }}>
              <span style={{ color: '#1E90FF', fontSize: '13px' }}>
                📹 Watch a quick video about Services
              </span>
            </div>

            {/* Info Box */}
            <div style={{
              backgroundColor: '#E3F2FD',
              border: '1px solid #90CAF9',
              borderRadius: '6px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#0D47A1', margin: '0 0 6px 0' }}>
                Click on each Service name to edit
              </h3>
              <p style={{ fontSize: '13px', color: '#1565C0', margin: 0 }}>
                Service Descriptions are shown throughout the MyClinicCore platform internally, in some client communications and in superbills.
              </p>
            </div>

            {/* Add New Service Form */}
            <div style={{ backgroundColor: '#113F67', padding: '20px', borderRadius: '6px', marginBottom: '24px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#FFFFFF', marginBottom: '16px' }}>
                Add New Service
              </h3>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#FFFFFF', marginBottom: '6px' }}>
                    Service
                  </label>
                  <select
                    value={newService.code}
                    onChange={(e) => setNewService({ ...newService, code: e.target.value })}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: '#0F4C75',
                      border: '1px solid #6CA6C1',
                      borderRadius: '4px',
                      color: '#FFFFFF',
                      fontSize: '14px',
                      width: '100%'
                    }}
                  >
                    <option value="12345">12345</option>
                    <option value="90834">90834 - Psychotherapy, 45 min</option>
                    <option value="90847">90847 - Family psychotherapy</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#FFFFFF', marginBottom: '6px' }}>
                    Description
                  </label>
                  <input
                    type="text"
                    value={newService.description}
                    onChange={(e) => setNewService({ ...newService, description: e.target.value })}
                    placeholder="Description"
                    style={{
                      padding: '8px 12px',
                      backgroundColor: '#0F4C75',
                      border: '1px solid #6CA6C1',
                      borderRadius: '4px',
                      color: '#FFFFFF',
                      fontSize: '14px',
                      width: '100%'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#FFFFFF', marginBottom: '6px' }}>
                    Rate
                  </label>
                  <input
                    type="number"
                    value={newService.rate}
                    onChange={(e) => setNewService({ ...newService, rate: parseFloat(e.target.value) || 0 })}
                    placeholder="0"
                    style={{
                      padding: '8px 12px',
                      backgroundColor: '#0F4C75',
                      border: '1px solid #6CA6C1',
                      borderRadius: '4px',
                      color: '#FFFFFF',
                      fontSize: '14px',
                      width: '100%'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'flex', alignItems: 'center', fontSize: '13px', fontWeight: 600, color: '#FFFFFF', marginBottom: '6px', gap: '6px' }}>
                    Default Duration
                    <span style={{ color: '#87CEEB', cursor: 'help', fontSize: '12px' }}>ⓘ</span>
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="number"
                      value={newService.duration}
                      onChange={(e) => setNewService({ ...newService, duration: parseInt(e.target.value) || 0 })}
                      style={{
                        padding: '8px 12px',
                        backgroundColor: '#0F4C75',
                        border: '1px solid #6CA6C1',
                        borderRadius: '4px',
                        color: '#FFFFFF',
                        fontSize: '14px',
                        width: '80px'
                      }}
                    />
                    <span style={{ color: '#E0E0E0', fontSize: '14px' }}>min</span>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={newService.isDefault}
                    onChange={(e) => setNewService({ ...newService, isDefault: e.target.checked })}
                    style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                  />
                  <span style={{ fontSize: '13px', color: '#E0E0E0' }}>Make this the default service</span>
                </label>

                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={newService.availableForOnlineRequests}
                    onChange={(e) => setNewService({ ...newService, availableForOnlineRequests: e.target.checked })}
                    style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                  />
                  <span style={{ fontSize: '13px', color: '#E0E0E0', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    Bill this code in units
                    <span style={{ color: '#87CEEB', cursor: 'help', fontSize: '12px' }}>ⓘ</span>
                  </span>
                </label>
              </div>

              <h4 style={{ fontSize: '15px', fontWeight: 600, color: '#FFFFFF', marginBottom: '12px' }}>
                Booking Options
              </h4>

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={newService.availableForOnlineRequests}
                  onChange={(e) => setNewService({ ...newService, availableForOnlineRequests: e.target.checked })}
                  style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                />
                <span style={{ fontSize: '13px', color: '#E0E0E0' }}>Available for online appointment requests</span>
              </label>

              <div style={{ marginLeft: '24px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={newService.allowForNewClients}
                    onChange={(e) => setNewService({ ...newService, allowForNewClients: e.target.checked })}
                    disabled={!newService.availableForOnlineRequests}
                    style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                  />
                  <span style={{ fontSize: '13px', color: newService.availableForOnlineRequests ? '#E0E0E0' : '#666' }}>Allow for New Clients</span>
                </label>

                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={newService.requireCallToRequest}
                    onChange={(e) => setNewService({ ...newService, requireCallToRequest: e.target.checked })}
                    disabled={!newService.availableForOnlineRequests}
                    style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                  />
                  <span style={{ fontSize: '13px', color: newService.availableForOnlineRequests ? '#E0E0E0' : '#666', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    Require Clients to call to request available appointment
                    <span style={{ color: '#87CEEB', cursor: 'help', fontSize: '12px' }}>ⓘ</span>
                  </span>
                </label>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '13px', color: '#E0E0E0' }}>
                <span>Block off</span>
                <input
                  type="number"
                  value={newService.blockOffBefore}
                  onChange={(e) => setNewService({ ...newService, blockOffBefore: parseInt(e.target.value) || 0 })}
                  style={{
                    padding: '6px 10px',
                    backgroundColor: '#0F4C75',
                    border: '1px solid #6CA6C1',
                    borderRadius: '4px',
                    color: '#FFFFFF',
                    fontSize: '14px',
                    width: '60px'
                  }}
                />
                <span>minutes before and</span>
                <input
                  type="number"
                  value={newService.blockOffAfter}
                  onChange={(e) => setNewService({ ...newService, blockOffAfter: parseInt(e.target.value) || 0 })}
                  style={{
                    padding: '6px 10px',
                    backgroundColor: '#0F4C75',
                    border: '1px solid #6CA6C1',
                    borderRadius: '4px',
                    color: '#FFFFFF',
                    fontSize: '14px',
                    width: '60px'
                  }}
                />
                <span>minutes after the appointment</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-start' }}>
              <button
                onClick={() => setShowAddServiceModal(false)}
                style={{
                  backgroundColor: '#6C757D',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 20px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveNewService}
                style={{
                  backgroundColor: '#1E90FF',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 20px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Save
              </button>
            </div>

            {/* Service List Below Modal */}
            <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #6CA6C1' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr 200px', gap: '16px', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #6CA6C1' }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#FFFFFF' }}>Services</div>
                <div></div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#FFFFFF', textAlign: 'right' }}>Appointment requests</div>
              </div>

              {services.map((service) => (
                <div
                  key={service.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '100px 1fr 200px',
                    gap: '16px',
                    padding: '16px 0',
                    borderBottom: '1px solid #6CA6C1'
                  }}
                >
                  <div style={{ fontSize: '14px', color: '#1E90FF', fontWeight: 500 }}>{service.code}</div>
                  <div>
                    <div style={{ fontSize: '14px', color: '#1E90FF', fontWeight: 500, marginBottom: '4px' }}>
                      {service.name}
                    </div>
                    <div style={{ fontSize: '13px', color: '#E0E0E0', marginBottom: '4px' }}>
                      {service.duration} minutes at ${service.rate}
                    </div>
                    {service.isDefault && (
                      <div style={{ fontSize: '13px', color: '#4CAF50' }}>
                        ✓ Default practice service
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', alignItems: 'center' }}>
                    <button
                      style={{
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: '#87CEEB',
                        fontSize: '18px',
                        cursor: 'pointer',
                        padding: '4px'
                      }}
                    >
                      📅
                    </button>
                    <button
                      style={{
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: '#87CEEB',
                        fontSize: '18px',
                        cursor: 'pointer',
                        padding: '4px'
                      }}
                    >
                      👤
                    </button>
                  </div>
                </div>
              ))}

              <div style={{ marginTop: '16px' }}>
                <button
                  onClick={() => setShowInactiveServices(!showInactiveServices)}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#1E90FF',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    padding: 0
                  }}
                >
                  Show inactive services
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicesProducts;
